<template>
  <div v-if="loading" class="row">
    <div class="col-12 text-center mt-5 mb-5">
      <b-spinner
        variant="primary"
        type="grow"
        :label="`${this.$t('reports.loadingLabel')}...`"
      ></b-spinner>
    </div>
  </div>
  <div v-else>
    <div class="row">
      <div class="col-12 select-area">
        <b-form-group
          :label="`${this.$t('reports.indicators.indicatorLabel')}:`"
          label-for="select-indicator"
        >
          <b-form-select
            id="select-indicator"
            v-model="selectedIndicator"
            :options="optionsIndicators"
            @change="setIndicatorsValues"
          ></b-form-select>
        </b-form-group>
        <b-form-group
          v-for="(indicatorValue, i) in selectedIndicatorValues"
          :label="`${$t('reports.comparativeIndicators.valueLabel')}
            ${i + 1}:`"
          :label-for="`indicator-value-${i + 1}`"
          :key="i"
        >
          <b-form-select
            :id="`indicator-value-${i + 1}`"
            v-model="indicatorValue.value"
            :options="optionsIndicatorsValues"
            @change="setChartByIndicatorValue(i)"
            :disabled="verifyDisabled(i)"
          ></b-form-select>
        </b-form-group>
      </div>
    </div>
    <div v-if="firstDisplayed" class="row">
      <div class="col-12 text-center mt-4">
        <p>
          <font-awesome-icon
            :icon="['fas', 'chart-line']"
            style="margin-right: 20px; font-size: 35px;"
          />
        </p>
        <h5>
          {{ this.$t('reports.comparativeIndicators.reportTitle') }}
        </h5>
        <p>{{ this.$t('reports.comparativeIndicators.fillFieldsMsg') }}</p>
      </div>
    </div>
    <div v-else class="row">
      <div class="col-12 mt-4">
        <highcharts :options="chartOptions"></highcharts>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isBefore } from 'date-fns';
import api from '../../../../services/api';
import eventBus from '../../../../eventBus';

// teste

export default {
  props: ['accumulated'],
  data: () => ({
    loading: true,
    userAuth: null,
    emptyState: true,
    firstDisplayed: true,
    loadingSelects: true,
    period: 'monthly',
    selectedIndicator: null,
    optionsIndicators: [],
    selectedIndicatorValues: [
      {
        value: null,
        response: {
        },
        choosed: false,
      },
      {
        value: null,
        response: {
        },
        choosed: false,
      },
      {
        value: null,
        response: {
        },
        choosed: false,
      },
      {
        value: null,
        response: {
        },
        choosed: false,
      },
    ],
    optionsIndicatorsValues: [],
    chartOptions: {},
    yAxisTitle: null,
  }),

  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('companies', ['myMetric']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('calendar', ['calendarDates']),
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.optionsIndicators = [
      {
        value: null,
        text: this.$t('reports.comparativeIndicators.selIndicatorLabel'),
      },
    ];

    this.optionsIndicatorsValues = [
      {
        value: null,
        text: this.$t('reports.comparativeIndicators.selValueLabel'),
      },
    ];

    this.chartOptions = {
      chart: {
        type: 'line',
      },
      title: {
        text: this.$t('reports.comparativeIndicators.reportTitle'),
      },
      xAxis: {
        categories: [],
      },
      yAxis: {
        title: {
          text: this.yAxisTitle,
        },
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: true,
          },
          enableMouseTracking: false,
        },
      },
      series: [],
    };

    this.changeYAxisTitle();
  },

  async mounted() {
    await this.getIndicators();

    this.loading = false;

    eventBus.$on('accumulated-report', (checked) => {
      this.accumulated = checked;

      if (this.chartOptions.series.length > 0) {
        this.updateChartCategories();

        this.updateChartSeries();
      }
    });

    eventBus.$on('data-source', (checked) => {
      this.period = checked;
      // if (checked) {
      //   this.period = 'monthly';
      // } else {
      //   this.period = 'daily';
      // }

      this.updateChartCategories();

      this.updateChartSeries();
    });
  },

  methods: {
    async getIndicators() {
      this.loading = true;

      this.loadingSelects = true;
      this.firstDisplayed = true;
      this.selectedIndicator = null;

      this.chartOptions.xAxis.categories = [];

      this.chartOptions.series = [];

      this.selectedIndicatorValues.forEach((el) => {
        el.value = null;
        el.response = {
        };
        el.choosed = false;
      });

      this.optionsIndicators = [
        {
          value: null,
          text: this.$t('reports.comparativeIndicators.selIndicatorLabel'),
        },
      ];

      this.optionsIndicatorsValues = [
        {
          value: null,
          text: this.$t('reports.comparativeIndicators.selValueLabel'),
        },
      ];

      const response = await api.get(
        `indicators/distinct/${this.userAuth.companyId}`,
        {
          headers: { 'x-access-token': this.userAuth.token },
          params: {
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            groupId: this.actionGroupData._id,
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          },
        },
      );

      response.data.indicators.forEach((indicator) => {
        this.optionsIndicators.push({
          value: indicator.key,
          text: indicator.text,
        });
      });

      this.loading = false;
    },

    async setIndicatorsValues() {
      if (this.selectedIndicator) {
        this.loadingSelects = true;
        this.firstDisplayed = true;

        this.chartOptions.xAxis.categories = [];

        this.chartOptions.series = [];

        this.optionsIndicatorsValues = [
          {
            value: null,
            text: `${this.$t(
              'reports.comparativeIndicators.selValueLabel',
            )}...`,
          },
        ];

        this.selectedIndicatorValues.forEach((el) => {
          el.value = null;
          el.choosed = false;
        });

        const response = await api.get(
          `${process.env.API_ROOT}/reports/indicator-values/${this.userAuth.companyId}`,
          {
            headers: { 'x-access-token': this.userAuth.token },
            params: {
              actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
              indicatorValue: this.selectedIndicator,
              metric: this.myMetric ? this.myMetric : null,
              startDate: this.calendarDates.start
                ? this.calendarDates.start
                : null,
              endDate: this.calendarDates.end ? this.calendarDates.end : null,
              ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
              ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
              groupId: this.actionGroupData._id,
            },
          },
        );

        response.data.map((result) => {
          this.optionsIndicatorsValues.push({
            value: result.key,
            text: result.text,
          });
        });

        this.optionsIndicatorsValues[0].text = this.$t(
          'reports.comparativeIndicators.selValueLabel',
        );

        this.loadingSelects = false;
      } else {
        this.selectedIndicatorValue = null;

        this.optionsIndicatorValue = [
          {
            value: null,
            text: this.$t('reports.comparativeIndicators.selValueLabel'),
          },
        ];
      }
    },

    async setChartByIndicatorValue(index) {
      const value = this.selectedIndicatorValues[index].value;

      if (value) {
        this.loading = true;
        this.selectedIndicatorValues[index].choosed = true;

        const response = await api.get(
          `/reports/comparative-indicators/${this.userAuth.companyId}`,
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
            params: {
              indicator: this.selectedIndicator,
              indicatorValue: value,
              actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
              metric: this.myMetric ? this.myMetric : null,
              startDate: this.calendarDates.start
                ? this.calendarDates.start
                : null,
              endDate: this.calendarDates.end ? this.calendarDates.end : null,
              ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
              ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
              groupId: this.actionGroupData._id,
              reportType: this.period === 'daily' ? 'day' : 'month'
            },
          },
        );

        this.selectedIndicatorValues[index].response = response.data;

        this.updateChartCategories();

        this.updateChartSeries();

        this.firstDisplayed = false;
        this.loading = false;
      } else {
        this.chartOptions.series[index].name = '';
        this.chartOptions.series[index].data = [];

        this.selectedIndicatorValues[index].value = null;
        this.selectedIndicatorValues[index].response = {
          daily: { categories: [], name: null, nps: [], npsAccumulated: [] },
          monthly: { categories: [], name: null, nps: [], npsAccumulated: [] },
        };

        this.updateChartCategories(index);
        this.updateChartSeries(index);
      }
    },

    updateChartCategories() {
      this.chartOptions.xAxis.categories = [];

      this.selectedIndicatorValues.forEach((indicatorValue) => {
        if(indicatorValue.value) {
        indicatorValue.response.categories.forEach((category) => {
          const dateFound = this.chartOptions.xAxis.categories.findIndex(
            (el) => el === category,
          );

          if (dateFound < 0) {
            const isBeforeIndex = this.chartOptions.xAxis.categories.findIndex(
              (el) =>
                isBefore(
                  new Date(this.convertDate(category)),
                  new Date(this.convertDate(el)),
                ),
            );

            if (isBeforeIndex >= 0) {
              this.chartOptions.xAxis.categories.splice(
                isBeforeIndex,
                0,
                category,
              );
            } else {
              this.chartOptions.xAxis.categories.push(category);
            }
          }
        });
        }
      });
    },

    updateChartSeries() {
      this.chartOptions.series = [];

      this.selectedIndicatorValues.forEach((indicatorValue, index) => {
        if (indicatorValue.value) {
          this.chartOptions.series[index] = {
            name: indicatorValue.response.name,
            data: [],
          };

          let previousValue = null;

          this.chartOptions.xAxis.categories.forEach((category) => {
            const npsIndex = indicatorValue.response.categories.findIndex((date) =>
            date === category);

            if (npsIndex >= 0) {
              const npsValue = this.accumulated
                ? indicatorValue.response.npsAccumulated[npsIndex]
                : indicatorValue.response.nps[npsIndex];

              this.chartOptions.series[index].data.push(npsValue);

              previousValue = npsValue;
            } else {
              this.chartOptions.series[index].data.push(
                this.accumulated ? previousValue : null,
              );
            }
          });
        }
      });
    },

    convertDate(date) {
      const dates = date.split('/');
      if (dates.length > 2) {
        return `${dates[1]}/${dates[0]}/${dates[2]}`;
      }
      return `${dates[0]}/01/${dates[1]}`;
    },

    verifyDisabled(index) {
      if (this.loadingSelects) {
        return true;
      }

      if (this.selectedIndicatorValues[index - 1]) {
        if (this.selectedIndicatorValues[index - 1].choosed === false) {
          return true;
        }
      }

      return false;
    },

    changeYAxisTitle() {
      if (this.myMetric.indexOf('nps') >= 0) {
        this.yAxisTitle = 'NPS';
      } else if (this.myMetric.indexOf('ces') >= 0) {
        this.yAxisTitle = 'CES';
      } else if (this.myMetric.indexOf('csat') >= 0) {
        this.yAxisTitle = 'CSAT';
      } else if (this.myMetric.indexOf('stars') >= 0) {
        this.yAxisTitle = '5Stars';
      }

      this.chartOptions.yAxis.title.text = this.yAxisTitle;
    },
  },

  watch: {
    actionGroupData: async function watchActionGroupData() {
      await this.getIndicators();
    },

    myActionId: async function watchMyActionId() {
      await this.getIndicators();
    },

    myActionsIds: async function watchMyActionsIds() {
      await this.getIndicators();
    },

    myIh1: async function watchMyIh1() {
      await this.getIndicators();
    },

    myHierarchies1: async function watchMyHierarchies1() {
      await this.getIndicators();
    },

    myHierarchies2: async function watchMyHierarchies2() {
      await this.getIndicators();
    },

    myIh2: async function watchMyIh2() {
      await this.getIndicators();
    },

    calendarDates: async function watchDates() {
      await this.getIndicators();
    },

    myMetric: async function watchMyMetric() {
      await this.getIndicators();
      this.changeYAxisTitle();
    },
  },

  beforeDestroy() {
    eventBus.$off('accumulated-report');
    eventBus.$off('data-source');
  },
};
</script>

<style scoped lang="scss">
.select-area {
  display: flex;
  flex-direction: row;

  select {
    width: 215px;
    margin-right: 15px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/comparative-indicators/index.vue